import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Save, AlertCircle, DollarSign, Percent, Hash, CheckCircle, TrendingUp, Users, Building } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

export default function ApprovalThresholdsManager() {
  const [services, setServices] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('global');

  // Global default thresholds
  const [globalThresholds, setGlobalThresholds] = useState({
    manager: {
      gpm_threshold: 55,
      min_revenue: 10000,
      max_revenue: 100000,
      transaction_count_threshold: 1000,
      net_revenue_threshold: 25000,
      enabled: {
        gpm_threshold: true,
        min_revenue: true,
        max_revenue: true,
        transaction_count_threshold: true,
        net_revenue_threshold: true
      }
    },
    ceo_cfo: {
      gpm_threshold: 48,
      min_revenue: 5000,
      max_revenue: 500000,
      transaction_count_threshold: 5000,
      net_revenue_threshold: 50000,
      enabled: {
        gpm_threshold: true,
        min_revenue: true,
        max_revenue: true,
        transaction_count_threshold: true,
        net_revenue_threshold: true
      }
    }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [servicesRes, thresholdsRes] = await Promise.all([
        api.get('/admin/services'),
        api.get('/approval-thresholds')
      ]);
      
      const servicesData = servicesRes.data.services || [];
      setServices(servicesData);
      
      const thresholdsData = thresholdsRes.data;
      
      // Safely merge global thresholds with defaults
      if (thresholdsData.global) {
        setGlobalThresholds(prev => ({
          manager: {
            ...prev.manager,
            ...(thresholdsData.global.manager || {})
          },
          ceo_cfo: {
            ...prev.ceo_cfo,
            ...(thresholdsData.global.ceo_cfo || {})
          }
        }));
      }
      
      if (thresholdsData.service_specific) {
        setThresholds(thresholdsData.service_specific);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load approval thresholds');
      }
    } finally {
      setLoading(false);
    }
  };

  const saveThresholds = async () => {
    try {
      setSaving(true);
      
      await api.post('/approval-thresholds', {
        global: globalThresholds,
        service_specific: thresholds
      });
      
      toast.success('✅ Approval thresholds saved successfully');
    } catch (error) {
      console.error('Error saving thresholds:', error);
      toast.error('❌ Failed to save approval thresholds');
    } finally {
      setSaving(false);
    }
  };

  const updateGlobalThreshold = useCallback((level, field, value) => {
    setGlobalThresholds(prev => ({
      ...prev,
      [level]: {
        ...prev[level],
        [field]: value === '' ? 0 : (isNaN(parseFloat(value)) ? prev[level][field] : parseFloat(value))
      }
    }));
  }, []);

  const toggleGlobalThreshold = useCallback((level, field) => {
    setGlobalThresholds(prev => ({
      ...prev,
      [level]: {
        ...prev[level],
        enabled: {
          ...prev[level].enabled,
          [field]: !prev[level].enabled[field]
        }
      }
    }));
  }, []);

  const updateServiceThreshold = useCallback((serviceId, level, field, value) => {
    setThresholds(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [level]: {
          ...prev[serviceId]?.[level],
          [field]: value === '' ? 0 : (isNaN(parseFloat(value)) ? prev[serviceId]?.[level]?.[field] || 0 : parseFloat(value))
        }
      }
    }));
  }, []);

  const toggleServiceThreshold = useCallback((serviceId, level, field) => {
    setThresholds(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [level]: {
          ...prev[serviceId]?.[level],
          enabled: {
            ...prev[serviceId]?.[level]?.enabled,
            [field]: !prev[serviceId]?.[level]?.enabled?.[field]
          }
        }
      }
    }));
  }, []);

// Controlled input component with local state to prevent focus loss
const ThresholdInput = React.memo(({ 
  label, 
  value, 
  onChange, 
  icon: Icon, 
  type = "number", 
  suffix = "", 
  tooltip = "", 
  enabled = true, 
  onToggle 
}) => {
  const [localValue, setLocalValue] = React.useState(value);
  
  // Sync local value when prop changes from external source
  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };
  
  const handleBlur = () => {
    // Only update parent on blur
    onChange(localValue);
  };
  
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700 flex items-center gap-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={onToggle}
          className="w-4 h-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500 cursor-pointer"
          title={enabled ? "Disable this threshold" : "Enable this threshold"}
        />
        <Icon className={`w-4 h-4 ${enabled ? 'text-slate-500' : 'text-slate-300'}`} />
        <span className={enabled ? '' : 'text-slate-400'}>{label}</span>
        {tooltip && (
          <span className="text-xs text-slate-500 italic" title={tooltip}>ℹ️</span>
        )}
      </label>
      <div className="relative">
        <input
          type={type}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={!enabled}
          className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-lg font-semibold transition-all ${
            enabled 
              ? 'border-slate-300 bg-white text-slate-900' 
              : 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
          }`}
        />
        {suffix && (
          <span className={`absolute right-3 top-1/2 -translate-y-1/2 font-medium ${enabled ? 'text-slate-500' : 'text-slate-300'}`}>
            {suffix}
          </span>
        )}
      </div>
      {tooltip && (
        <p className={`text-xs italic ${enabled ? 'text-slate-500' : 'text-slate-400'}`}>{tooltip}</p>
      )}
    </div>
  );
});

// Memoized ApprovalCard component to prevent unnecessary re-renders
const ApprovalCard = React.memo(({ level, title, description, icon: Icon, color, thresholdData, updateFn, toggleFn }) => (
  <div className={`bg-white border-2 ${color} rounded-xl p-6 space-y-4 shadow-sm hover:shadow-md transition-shadow`}>
      <div className="flex items-start gap-3">
        <div className={`p-3 ${color.replace('border', 'bg').replace('600', '100')} rounded-lg`}>
          <Icon className={`w-6 h-6 ${color.replace('border', 'text')}`} />
        </div>
        <div className="flex-1">
          <h4 className="text-lg font-bold text-slate-900">{title}</h4>
          <p className="text-sm text-slate-600 mt-1">{description}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
        <ThresholdInput
          label="Min GPM %"
          value={thresholdData.gpm_threshold}
          onChange={(val) => updateFn(level, 'gpm_threshold', val)}
          enabled={thresholdData.enabled?.gpm_threshold ?? true}
          onToggle={() => toggleFn(level, 'gpm_threshold')}
          icon={Percent}
          suffix="%"
        />
        
        {/* Both Manager and CFO/CEO levels now have Min/Max Revenue ranges */}
        <ThresholdInput
          label="Min Gross Revenue"
          value={thresholdData.min_revenue}
          onChange={(val) => updateFn(level, 'min_revenue', val)}
          enabled={thresholdData.enabled?.min_revenue ?? true}
          onToggle={() => toggleFn(level, 'min_revenue')}
          icon={DollarSign}
          suffix="$"
          tooltip={level === 'manager' 
            ? "Proposals below this amount require CEO/CFO approval (too small)" 
            : "Very small deals requiring executive review"}
        />
        <ThresholdInput
          label="Max Gross Revenue"
          value={thresholdData.max_revenue}
          onChange={(val) => updateFn(level, 'max_revenue', val)}
          enabled={thresholdData.enabled?.max_revenue ?? true}
          onToggle={() => toggleFn(level, 'max_revenue')}
          icon={DollarSign}
          suffix="$"
          tooltip={level === 'manager' 
            ? "Proposals above this amount require CEO/CFO approval (needs visibility)" 
            : "Very large deals requiring executive oversight"}
        />
        
        <ThresholdInput
          label="Max Transactions"
          value={thresholdData.transaction_count_threshold}
          onChange={(val) => updateFn(level, 'transaction_count_threshold', val)}
          enabled={thresholdData.enabled?.transaction_count_threshold ?? true}
          onToggle={() => toggleFn(level, 'transaction_count_threshold')}
          icon={Hash}
        />
        <ThresholdInput
          label="Max Net Revenue"
          value={thresholdData.net_revenue_threshold}
          onChange={(val) => updateFn(level, 'net_revenue_threshold', val)}
          enabled={thresholdData.enabled?.net_revenue_threshold ?? true}
          onToggle={() => toggleFn(level, 'net_revenue_threshold')}
          icon={TrendingUp}
          suffix="$"
          tooltip={level === 'ceo_cfo' ? "Net revenue over this amount requires CFO/CEO approval" : undefined}
        />
      </div>
    </div>
  ));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const servicesWithCustom = services.filter(s => thresholds[s.id]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-2xl p-6 border border-indigo-100">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Shield className="w-8 h-8 text-indigo-600" />
              Approval Thresholds
            </h2>
            <p className="text-slate-600 mt-2 text-base">
              Configure approval requirements to route proposals to the right approvers automatically
            </p>
          </div>
          <button
            onClick={saveThresholds}
            disabled={saving}
            className="btn-primary flex items-center gap-2 px-6 py-3 text-lg font-semibold shadow-lg"
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-5">
        <div className="flex gap-3">
          <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-base font-bold text-blue-900 mb-2">📋 How Hierarchical Approval Works</h4>
            <div className="grid md:grid-cols-2 gap-3 text-sm text-blue-800">
              <div className="space-y-1">
                <p className="font-semibold">✅ Manager Approval Only:</p>
                <ul className="ml-5 space-y-0.5 list-disc">
                  <li>Gross revenue within Manager's Min-Max range</li>
                  <li>GPM meets Manager threshold</li>
                  <li>Net revenue below Manager threshold</li>
                  <li>Transactions within limits</li>
                </ul>
              </div>
              <div className="space-y-1">
                <p className="font-semibold">🔒 Manager + CEO/CFO Approval:</p>
                <ul className="ml-5 space-y-0.5 list-disc">
                  <li>Gross revenue <strong>outside</strong> Manager's range (too small or large)</li>
                  <li>GPM below CEO/CFO threshold</li>
                  <li>Net revenue exceeds CEO/CFO threshold ($50K)</li>
                  <li>Transactions/volume exceed CEO/CFO limits</li>
                </ul>
              </div>
            </div>
            <p className="mt-3 text-sm text-blue-700 bg-blue-100 rounded px-3 py-2">
              ⚡ <strong>Hierarchical Flow:</strong> Manager approves first. If proposal requires CEO/CFO approval, it routes to them only after Manager approval. Both approvals are required for deals outside Manager's thresholds.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-1">
          <button
            onClick={() => setActiveTab('global')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'global'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
            }`}
          >
            <Building className="w-5 h-5" />
            Global Defaults
            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
              All Services
            </span>
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'services'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
            }`}
          >
            <Users className="w-5 h-5" />
            Service-Specific
            {servicesWithCustom.length > 0 && (
              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-bold">
                {servicesWithCustom.length} Custom
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'global' ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ApprovalCard
              level="manager"
              title="Manager Approval"
              description="Requires approval from managers or higher-level approvers"
              icon={Users}
              color="border-amber-600"
              thresholdData={globalThresholds.manager}
              updateFn={updateGlobalThreshold}
              toggleFn={toggleGlobalThreshold}
            />
            <ApprovalCard
              level="ceo_cfo"
              title="CEO/CFO Approval"
              description="Requires approval from executive leadership (CEO or CFO)"
              icon={Shield}
              color="border-rose-600"
              thresholdData={globalThresholds.ceo_cfo}
              updateFn={updateGlobalThreshold}
              toggleFn={toggleGlobalThreshold}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <p className="text-sm text-slate-700">
              <strong>Service-Specific Overrides:</strong> Configure custom thresholds for individual services. 
              These will override the global defaults. Services without custom settings use global defaults.
            </p>
          </div>

          {services.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-slate-600">No services found. Create services first to configure service-specific thresholds.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {services.map(service => {
                // Ensure we have proper defaults for both levels
                const defaultManagerThresholds = {
                  gpm_threshold: globalThresholds.manager?.gpm_threshold || 55,
                  min_revenue: globalThresholds.manager?.min_revenue || 10000,
                  max_revenue: globalThresholds.manager?.max_revenue || 100000,
                  transaction_count_threshold: globalThresholds.manager?.transaction_count_threshold || 1000,
                  net_revenue_threshold: globalThresholds.manager?.net_revenue_threshold || 25000,
                  enabled: {
                    gpm_threshold: true,
                    min_revenue: true,
                    max_revenue: true,
                    transaction_count_threshold: true,
                    net_revenue_threshold: true
                  }
                };
                
                const defaultCeoCfoThresholds = {
                  gpm_threshold: globalThresholds.ceo_cfo?.gpm_threshold || 48,
                  min_revenue: globalThresholds.ceo_cfo?.min_revenue || 5000,
                  max_revenue: globalThresholds.ceo_cfo?.max_revenue || 500000,
                  transaction_count_threshold: globalThresholds.ceo_cfo?.transaction_count_threshold || 5000,
                  net_revenue_threshold: globalThresholds.ceo_cfo?.net_revenue_threshold || 50000,
                  enabled: {
                    gpm_threshold: true,
                    min_revenue: true,
                    max_revenue: true,
                    transaction_count_threshold: true,
                    net_revenue_threshold: true
                  }
                };
                
                const serviceThresholds = {
                  manager: {
                    ...defaultManagerThresholds,
                    ...(thresholds[service.id]?.manager || {})
                  },
                  ceo_cfo: {
                    ...defaultCeoCfoThresholds,
                    ...(thresholds[service.id]?.ceo_cfo || {})
                  }
                };

                return (
                  <div key={service.id} className="bg-white border-2 border-slate-200 rounded-xl p-6 space-y-4">
                    <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                      <h3 className="text-xl font-bold text-slate-900">{service.name}</h3>
                      {thresholds[service.id] && (
                        <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-bold">
                          Custom
                        </span>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold text-amber-700 mb-3">Manager Level</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <ThresholdInput
                            label="Min GPM %"
                            value={serviceThresholds.manager.gpm_threshold}
                            onChange={(val) => updateServiceThreshold(service.id, 'manager', 'gpm_threshold', val)}
                            enabled={serviceThresholds.manager.enabled?.gpm_threshold ?? true}
                            onToggle={() => toggleServiceThreshold(service.id, 'manager', 'gpm_threshold')}
                            icon={Percent}
                            suffix="%"
                          />
                          <ThresholdInput
                            label="Min Revenue"
                            value={serviceThresholds.manager.min_revenue}
                            onChange={(val) => updateServiceThreshold(service.id, 'manager', 'min_revenue', val)}
                            enabled={serviceThresholds.manager.enabled?.min_revenue ?? true}
                            onToggle={() => toggleServiceThreshold(service.id, 'manager', 'min_revenue')}
                            icon={DollarSign}
                            suffix="$"
                          />
                          <ThresholdInput
                            label="Max Revenue"
                            value={serviceThresholds.manager.max_revenue}
                            onChange={(val) => updateServiceThreshold(service.id, 'manager', 'max_revenue', val)}
                            enabled={serviceThresholds.manager.enabled?.max_revenue ?? true}
                            onToggle={() => toggleServiceThreshold(service.id, 'manager', 'max_revenue')}
                            icon={DollarSign}
                            suffix="$"
                          />
                          <ThresholdInput
                            label="Max Transactions"
                            value={serviceThresholds.manager.transaction_count_threshold}
                            onChange={(val) => updateServiceThreshold(service.id, 'manager', 'transaction_count_threshold', val)}
                            enabled={serviceThresholds.manager.enabled?.transaction_count_threshold ?? true}
                            onToggle={() => toggleServiceThreshold(service.id, 'manager', 'transaction_count_threshold')}
                            icon={Hash}
                          />
                          <ThresholdInput
                            label="Max Net Revenue"
                            value={serviceThresholds.manager.net_revenue_threshold}
                            onChange={(val) => updateServiceThreshold(service.id, 'manager', 'net_revenue_threshold', val)}
                            enabled={serviceThresholds.manager.enabled?.net_revenue_threshold ?? true}
                            onToggle={() => toggleServiceThreshold(service.id, 'manager', 'net_revenue_threshold')}
                            icon={TrendingUp}
                            suffix="$"
                          />
                        </div>
                      </div>

                      <div className="pt-3 border-t border-slate-100">
                        <h4 className="text-sm font-semibold text-rose-700 mb-3">CEO/CFO Level</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <ThresholdInput
                            label="Min GPM %"
                            value={serviceThresholds.ceo_cfo.gpm_threshold}
                            onChange={(val) => updateServiceThreshold(service.id, 'ceo_cfo', 'gpm_threshold', val)}
                            enabled={serviceThresholds.ceo_cfo.enabled?.gpm_threshold ?? true}
                            onToggle={() => toggleServiceThreshold(service.id, 'ceo_cfo', 'gpm_threshold')}
                            icon={Percent}
                            suffix="%"
                          />
                          <ThresholdInput
                            label="Min Revenue"
                            value={serviceThresholds.ceo_cfo.min_revenue}
                            onChange={(val) => updateServiceThreshold(service.id, 'ceo_cfo', 'min_revenue', val)}
                            enabled={serviceThresholds.ceo_cfo.enabled?.min_revenue ?? true}
                            onToggle={() => toggleServiceThreshold(service.id, 'ceo_cfo', 'min_revenue')}
                            icon={DollarSign}
                            suffix="$"
                          />
                          <ThresholdInput
                            label="Max Revenue"
                            value={serviceThresholds.ceo_cfo.max_revenue}
                            onChange={(val) => updateServiceThreshold(service.id, 'ceo_cfo', 'max_revenue', val)}
                            enabled={serviceThresholds.ceo_cfo.enabled?.max_revenue ?? true}
                            onToggle={() => toggleServiceThreshold(service.id, 'ceo_cfo', 'max_revenue')}
                            icon={DollarSign}
                            suffix="$"
                          />
                          <ThresholdInput
                            label="Max Transactions"
                            value={serviceThresholds.ceo_cfo.transaction_count_threshold}
                            onChange={(val) => updateServiceThreshold(service.id, 'ceo_cfo', 'transaction_count_threshold', val)}
                            enabled={serviceThresholds.ceo_cfo.enabled?.transaction_count_threshold ?? true}
                            onToggle={() => toggleServiceThreshold(service.id, 'ceo_cfo', 'transaction_count_threshold')}
                            icon={Hash}
                          />
                          <ThresholdInput
                            label="Max Net Revenue"
                            value={serviceThresholds.ceo_cfo.net_revenue_threshold}
                            onChange={(val) => updateServiceThreshold(service.id, 'ceo_cfo', 'net_revenue_threshold', val)}
                            enabled={serviceThresholds.ceo_cfo.enabled?.net_revenue_threshold ?? true}
                            onToggle={() => toggleServiceThreshold(service.id, 'ceo_cfo', 'net_revenue_threshold')}
                            icon={TrendingUp}
                            suffix="$"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
